import type { Tenant } from '@t4s/api-client/model'
import { codeOf } from '../lib/api-error.ts'
import { messageFor } from '../lib/messages.ts'
import { Card, CardError, CardSkeleton } from './card.tsx'
import { CopyButton } from './copy-button.tsx'
import type { BrandingRead } from './overview-cards.tsx'

/**
 * The build key — docs/ux/PLATFORM-ADMIN.md §11.1 and §16.2.
 *
 * **The current key is shown, and it is shown plainly.** It is public and authorises nothing:
 * it ships inside the tenant's apps and is served, unauthenticated, by
 * `GET /branding/<slug>.json`. Masking it would be a gesture at a risk that does not exist,
 * and it would make the one thing a support conversation needs — "which key is that build
 * carrying?" — a click harder. Hence no Reveal, no timer, and a Copy control that is the
 * ordinary one.
 *
 * It is read from the **branding config document** (`GET …/branding-config`), the one place
 * the API states it for a live tenant; that read belongs to the page, which hands it down, so
 * the Branding card and this one cannot disagree about which tenant they are showing.
 *
 * **A rotation replaces the shown key in the same place** (§16.2). `rotated` is what the
 * rotation answered and when; it is shown until the document is written after that moment (a
 * re-read), and it is the only way the new key is visible when the document itself failed to load.
 *
 * **On a retired tenant there is no key and no button** (§16.4): its builds are already
 * refused, rotation would change nothing and the API answers it with `conflict`. Absent, not
 * disabled.
 */
export function BuildKeyCard({
  tenant,
  branding,
  rotated,
  onRotate,
}: {
  readonly tenant: Tenant | undefined
  readonly branding: BrandingRead
  /** What the last rotation answered, and when (ms). `undefined` until one has happened. */
  readonly rotated: { readonly key: string; readonly at: number } | undefined
  readonly onRotate: () => void
}) {
  const retired = tenant?.state === 'retired'
  // The freshest of the two: what the rotation answered, or the branding document if it has been
  // written since (a re-read after the rotation). Neither shadows the other for longer than it is
  // the newer — and with no document at all, the rotation's answer is all there is.
  const documentIsNewer = rotated !== undefined && branding.dataUpdatedAt > rotated.at
  const shownKey =
    rotated !== undefined && !documentIsNewer ? rotated.key : (branding.data?.buildKey ?? undefined)

  if (retired) {
    return (
      <Card
        wide
        title="Build key"
        description="This tenant is retired and its builds are already refused, so rotating its build key would change nothing. Rotation is not offered here."
      />
    )
  }

  return (
    <Card
      wide
      title="Build key"
      description="Public, not a credential: the value this tenant’s branded app sends to identify itself on every unauthenticated request. No masking, no expiry."
      busy={tenant === undefined || (shownKey === undefined && branding.isPending)}
      action={
        <button
          type="button"
          className="button button-danger"
          disabled={tenant === undefined}
          onClick={onRotate}
        >
          Rotate build key…
        </button>
      }
    >
      {shownKey !== undefined ? (
        <div className="card-body">
          <div className="copy-row">
            <code className="keybox" data-testid="build-key">
              {shownKey}
            </code>
            <CopyButton value={shownKey} noun="build key" />
          </div>
          {rotated === undefined ? null : (
            <p className="small secondary" data-testid="rotated-build-key">
              This is the new key. Give it to whoever cuts this tenant’s next build. Every build
              carrying the previous value stopped working the moment the rotation completed.
            </p>
          )}
        </div>
      ) : branding.isPending ? (
        <CardSkeleton lines={1} />
      ) : branding.isError ? (
        <CardError
          message={messageFor('branding', codeOf(branding.error))}
          onRetry={branding.refetch}
        />
      ) : (
        <div className="card-body">
          <p className="small secondary">No build key is recorded for this tenant.</p>
        </div>
      )}
    </Card>
  )
}
